import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import {
  Leaf,
  Shield,
  Truck,
  Award,
  Phone,
  MapPin,
  CheckCircle2,
  Recycle,
  Building2,
  Factory,
  Heart,
  Star,
  Moon,
  Sun,
  Users,
  TrendingUp,
  Clock,
  FileCheck,
  ShieldCheck,
  Droplets,
  Zap,
  Target,
  ArrowRight
} from 'lucide-react';

const LandingPage = () => {
  const [darkMode, setDarkMode] = useState(false);

  useEffect(() => {
    if (darkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [darkMode]);

  // Parallax Effect
  useEffect(() => {
    const handleScroll = () => {
      const parallaxElements = document.querySelectorAll('.parallax-bg');

      parallaxElements.forEach((element) => {
        const scrolled = window.pageYOffset;
        const rect = element.parentElement.getBoundingClientRect();
        const elementTop = rect.top + scrolled;
        const elementHeight = rect.height;

        // Only apply parallax when element is in viewport
        if (scrolled + window.innerHeight > elementTop && scrolled < elementTop + elementHeight) {
          const offset = (scrolled - elementTop) * 0.5;
          element.style.transform = `translateY(${offset}px)`;
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleWhatsAppClick = () => {
    window.open('https://wa.me/6285294174856?text=Hallo%20PT%20ALAM%20HIJAU%20PERTIWI%20,%20saya%20ingin%20konsultasi%20terkait%20jasa%20pengangkutan%20Limbah%20B3%20Yaa', '_blank');
  };

  const stats = [
    { number: '500+', label: 'Klien Terlayani', icon: Users },
    { number: '15+', label: 'Tahun Pengalaman', icon: Award },
    { number: '1000+', label: 'Ton Limbah Terangkut', icon: Truck },
    { number: '99.9%', label: 'Tingkat Keamanan', icon: ShieldCheck },
  ];

  const wasteTypes = [
    {
      icon: Factory,
      title: 'Limbah Industri Kimia',
      desc: 'Limbah hasil produksi kimia berbahaya, termasuk sisa reaksi kimia dan bahan kimia kedaluwarsa',
      image: 'https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/limbah-industri-kimia.webp'
    },
    {
      icon: Building2,
      title: 'Limbah Medis',
      desc: 'Limbah infeksius dan bahan berbahaya dari fasilitas kesehatan seperti jarum suntik, perban bekas, dan obat kedaluwarsa',
      image: 'https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/limbah-medis.webp'
    },
    {
      icon: Recycle,
      title: 'Limbah Elektronik B3',
      desc: 'Komponen elektronik mengandung logam berat seperti merkuri, timbal, dan kadmium',
      image: 'https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/limbah-elektronik.webp'
    },
    {
      icon: Droplets,
      title: 'Limbah Oli & Pelumas',
      desc: 'Oli bekas dan pelumas industri yang mengandung hidrokarbon dan logam berat',
      image: 'https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/limbah-oli-dan-pelumas.webp'
    },
    {
      icon: Factory,
      title: 'Limbah Laboratorium',
      desc: 'Bahan kimia dan reagen laboratorium, termasuk asam, basa, dan pelarut organik',
      image: 'https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/limbah-labolaturium.webp'
    },
    {
      icon: Zap,
      title: 'Limbah Cat & Solven',
      desc: 'Sisa cat, tiner, dan pelarut organik dari industri manufaktur dan otomotif',
      image: 'https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/limbah-cat-dan-solven.webp'
    },
    {
      icon: Recycle,
      title: 'Baterai & Aki Bekas',
      desc: 'Limbah baterai mengandung logam berat seperti nikel, kadmium, dan asam sulfat',
      image: 'https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/limbah-aki-dan-baterai-bekas.webp'
    },
    {
      icon: Leaf,
      title: 'Limbah Pestisida',
      desc: 'Sisa pestisida dan kemasan terkontaminasi dari sektor pertanian dan perkebunan',
      image: 'https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/limbah-pestisida.webp'
    },
  ];

  const certifications = [
    { 
      title: 'Akte Pendirian', 
      detail: 'No. 07 Tanggal 27 Juli 2019',
      year: '2019',
      icon: Award
    },
    { 
      title: 'No. SK KEMENHUB', 
      detail: 'NIB OOS RBA KBLI 49432:9120401872263007',
      year: '2021',
      icon: Award
    },
    { 
      title: 'Nomor Induk Berusaha', 
      detail: 'NIB : 9120401872263',
      year: '2020',
      icon: Award
    },
    { 
      title: 'Rekomendasi LH', 
      detail: 'NO. S.488/PSLB3-VPLB3/PPLB3/PLB.3/12/2021',
      year: '2021',
      icon: Award
    },
    { 
      title: 'NPWP', 
      detail: '92.350.440.1-432.000',
      year: '2019',
      icon: Award
    },
    { 
      title: 'Asuransi Liability', 
      detail: 'Policy No. : IP2109101900114',
      subtitle: 'Aswata - Asuransi Terpercaya',
      year: '2021',
      icon: Award
    },
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Konsultasi & Survey',
      desc: 'Tim kami melakukan survey lokasi dan identifikasi jenis limbah B3 yang akan diangkut',
      icon: FileCheck
    },
    {
      step: '02',
      title: 'Penawaran Harga',
      desc: 'Kami memberikan penawaran harga yang kompetitif dan transparan sesuai jenis dan volume limbah',
      icon: Target
    },
    {
      step: '03',
      title: 'Penjadwalan',
      desc: 'Koordinasi jadwal pengangkutan yang sesuai dengan kebutuhan operasional klien',
      icon: Clock
    },
    {
      step: '04',
      title: 'Pengangkutan Aman',
      desc: 'Armada khusus dan tim terlatih melakukan pengangkutan sesuai prosedur K3',
      icon: Truck
    },
    {
      step: '05',
      title: 'Dokumentasi Lengkap',
      desc: 'Manifest dan dokumen lengkap sesuai regulasi Kementerian Lingkungan Hidup',
      icon: ShieldCheck
    },
    {
      step: '06',
      title: 'Pelaporan',
      desc: 'Laporan pengangkutan dan sertifikat disposal untuk compliance perusahaan',
      icon: Award
    }
  ];

  const testimonials = [
    {
      company: 'RS Jasa Kartini Tasikmalaya',
      person: 'Dr. Siti Nurhaliza',
      position: 'Kepala Bagian Sanitasi',
      text: 'PT Alam Hijau Pertiwi sangat profesional dalam menangani limbah medis kami. Pengangkutan tepat waktu dan dokumentasi lengkap. Sudah 3 tahun kerjasama berjalan lancar.',
      rating: 5
    },
    {
      company: 'PT Karya Mandiri Tekstil',
      person: 'Budi Santoso',
      position: 'Manager HSE',
      text: 'Kerjasama selama 3 tahun berjalan sangat baik. Mereka memahami regulasi limbah B3 dan membantu kami tetap compliant dengan peraturan pemerintah.',
      rating: 5
    },
    {
      company: 'Laboratorium Kimia Unpad',
      person: 'Prof. Dr. Ahmad Fauzi',
      position: 'Kepala Laboratorium',
      text: 'Penanganan limbah laboratorium sangat aman dan terdokumentasi dengan baik. Tim yang responsif dan profesional. Highly recommended!',
      rating: 5
    },
    {
      company: 'RS Prasetya Bunda',
      person: 'Ir. Dewi Anggraini',
      position: 'Supervisor Fasilitas',
      text: 'Tim yang responsif dan bertanggung jawab. Pengangkutan limbah medis selalu on-time dan sesuai prosedur. Sangat puas dengan layanan mereka.',
      rating: 5
    }
  ];

  const features = [
    {
      icon: Shield,
      title: 'Aman & Terpercaya',
      desc: 'Armada khusus dengan standar keamanan tinggi untuk pengangkutan limbah B3'
    },
    {
      icon: Award,
      title: 'Bersertifikat Resmi',
      desc: 'Memiliki izin lengkap dari Kementerian Lingkungan Hidup dan Dinas terkait'
    },
    {
      icon: Truck,
      title: 'Armada Lengkap',
      desc: 'Berbagai jenis kendaraan khusus limbah B3 dengan tracking system'
    },
    {
      icon: Leaf,
      title: 'Ramah Lingkungan',
      desc: 'Komitmen penuh terhadap perlindungan lingkungan dan sustainability'
    }
  ];

  const whyChooseUs = [
    {
      icon: Clock,
      title: 'Pengangkutan Tepat Waktu',
      desc: 'Khusus Untuk Limbah Medis Tersedia Layanan 2 X 24 Jam.'
    },
    {
      icon: Zap,
      title: 'Integrasi Sistem Digital',
      desc: 'Pencatatan Menggunakan Festronik Yang Terkoneksi Langsung Dengan Akun OSS, Amdalnet, Dan Speed KLH.'
    },
    {
      icon: Users,
      title: 'Edukasi Klien',
      desc: 'Memberikan Pelatihan Khusus Untuk Koneksi Festronik Ke OSS/Amdalnet Dan Speed Kepada Petugas Kesling (Kesehatan Lingkungan).'
    },
    {
      icon: Truck,
      title: 'Fasilitas Pinjaman',
      desc: 'Memberikan Pinjaman Wheelie Bin (Wadah Limbah Beroda).'
    },
    {
      icon: Target,
      title: 'Transparansi',
      desc: 'Kunjungan Bersama Penghasil Limbah B3 Ke Lokasi Plant Pemusnahan.'
    }
  ];

  const galleryImages = [
    {
      url: 'https://images.unsplash.com/photo-1641893823219-38b433f736c0',
      title: 'Tim Safety Inspection',
      desc: 'Proses inspeksi keamanan limbah'
    },
    {
      url: 'https://images.unsplash.com/photo-1768796372343-99ed316eb5ef',
      title: 'Dokumentasi Manifest',
      desc: 'Pencatatan manifest limbah B3'
    },
    {
      url: 'https://images.unsplash.com/photo-1694090709409-78b1175a54a8',
      title: 'Armada Transportasi',
      desc: 'Truk khusus pengangkutan limbah'
    },
    {
      url: 'https://images.unsplash.com/photo-1768796372882-8b67936af681',
      title: 'Quality Control',
      desc: 'Pemeriksaan dokumen dan packaging'
    },
    {
      url: 'https://images.unsplash.com/photo-1759159091245-fc043faacddc',
      title: 'Hazmat Protection',
      desc: 'Penanganan limbah berbahaya'
    },
    {
      url: 'https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/pekerja-1.webp',
      title: 'Safe Loading',
      desc: 'Proses loading dengan prosedur K3'
    }
  ];

  // SVG map silhouettes for each region (stylized outlines)
  const mapSilhouettes = {
    'Jakarta': 'M20,25 L35,20 L50,22 L55,30 L52,45 L45,55 L35,58 L25,55 L18,45 L15,35 Z',
    'Bogor': 'M15,20 L40,15 L55,25 L60,40 L55,55 L45,65 L30,68 L15,60 L10,45 L12,30 Z',
    'Bekasi': 'M25,18 L45,15 L58,22 L62,38 L58,52 L48,60 L32,62 L20,55 L15,40 L18,28 Z',
    'Purwakarta': 'M20,22 L42,18 L55,28 L58,42 L52,56 L40,62 L25,58 L15,48 L12,35 Z',
    'Bandung': 'M10,30 L30,15 L55,18 L68,30 L65,50 L50,65 L28,68 L12,55 L8,42 Z',
    'Tangerang': 'M22,20 L48,18 L60,28 L58,45 L50,58 L35,62 L20,55 L15,42 L18,30 Z',
    'Subang': 'M18,25 L38,18 L55,22 L62,35 L58,52 L45,62 L28,60 L15,50 L12,38 Z',
    'Ciamis': 'M12,28 L35,20 L58,25 L65,40 L60,55 L45,65 L25,62 L10,50 L8,38 Z',
    'Karawang': 'M20,22 L45,16 L60,26 L65,42 L58,58 L42,65 L25,60 L12,48 L15,32 Z',
    'Pangandaran': 'M25,30 L45,22 L60,32 L55,48 L45,58 L30,55 L20,45 L22,35 Z M58,45 L68,52 L65,62 L55,58 Z',
    'Cilacap': 'M8,35 L28,25 L52,28 L68,38 L65,52 L50,62 L30,65 L12,55 L5,45 Z',
    'Cianjur': 'M15,28 L40,20 L58,28 L62,45 L55,60 L38,65 L20,58 L10,45 L12,35 Z',
    'Tasikmalaya': 'M10,32 L32,22 L55,25 L65,38 L62,55 L48,65 L28,68 L12,58 L6,45 Z',
    'Kota Sukabumi': 'M22,25 L42,18 L58,26 L62,42 L55,58 L40,64 L24,60 L15,48 L18,35 Z',
    'Sumedang': 'M18,28 L38,20 L55,25 L60,40 L55,55 L42,62 L25,58 L14,48 L15,38 Z',
    'Majalengka': 'M20,25 L42,18 L58,28 L62,42 L56,56 L42,62 L26,58 L15,48 L16,35 Z',
    'Kab Sukabumi': 'M8,32 L30,18 L55,22 L68,35 L65,55 L48,68 L25,65 L10,52 L5,42 Z',
    'Kota Banjar': 'M25,28 L45,22 L58,30 L55,45 L45,55 L30,52 L22,42 L24,35 Z',
    'Kota Depok': 'M28,25 L48,20 L58,28 L55,42 L48,52 L35,55 L25,48 L22,38 L25,30 Z',
  };

  const clientLocations = [
    { city: 'Jakarta', customers: 95 },
    { city: 'Bogor', customers: 89 },
    { city: 'Bekasi', customers: 16 },
    { city: 'Purwakarta', customers: 26 },
    { city: 'Bandung', customers: 93 },
    { city: 'Tangerang', customers: 27 },
    { city: 'Subang', customers: 2 },
    { city: 'Ciamis', customers: 26 },
    { city: 'Karawang', customers: 6 },
    { city: 'Pangandaran', customers: 6 },
    { city: 'Cilacap', customers: 23 },
    { city: 'Cianjur', customers: 5 },
    { city: 'Tasikmalaya', customers: 114 },
    { city: 'Kota Sukabumi', customers: 64 },
    { city: 'Sumedang', customers: 24 },
    { city: 'Majalengka', customers: 6 },
    { city: 'Kab Sukabumi', customers: 120 },
    { city: 'Kota Banjar', customers: 16 },
    { city: 'Kota Depok', customers: 3 },
  ];

  return (
    <div className="min-h-screen bg-page transition-colors duration-300">
      {/* Header/Navigation */}
      <header className="network-header">
        <div className="nav-wrapper">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
              <Leaf className="w-6 h-6 text-brand-dark" />
            </div>
            <span className="network-logo">PT ALAM HIJAU PERTIWI</span>
          </div>

          <nav className="network-nav">
            <a href="#tentang" className="network-nav-link">Tentang</a>
            <a href="#layanan" className="network-nav-link">Layanan</a>
            <a href="#proses" className="network-nav-link">Proses</a>
            <a href="#galeri" className="network-nav-link">Galeri</a>
            <a href="#testimoni" className="network-nav-link">Testimoni</a>
            <a href="#klien" className="network-nav-link">Klien</a>
            <a href="#kontak" className="network-nav-link">Kontak</a>
            <div className="flex items-center gap-2 ml-4">
              <Sun className="w-4 h-4 text-white" />
              <Switch checked={darkMode} onCheckedChange={setDarkMode} />
              <Moon className="w-4 h-4 text-white" />
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Section with Abstract Shapes */}
      <section className="relative pt-32 pb-24 px-4 overflow-hidden abstract-bg">
        <div className="absolute inset-0 z-0">
          <img
            src="https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/bg-1.webp"
            alt="Green Environment"
            className="w-full h-full object-cover opacity-10 dark:opacity-5"
            loading="lazy"
            decoding="async"
          />
        </div>

        {/* Hero Image with Abstract Shape - Similar to reference */}
        <div className="hero-shape hidden lg:block">
          <img
            src="https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/pekerja-1.webp"
            alt="Professional Worker"
            loading="lazy"
            decoding="async"
          />
        </div>

        <div className="container relative z-10 max-w-6xl mx-auto">
          <div className="hero-content-wrapper max-w-3xl space-y-6 animated fadeIn">
            <Badge className="bg-brand-primary text-brand-dark text-sm px-4 py-2">
              <Heart className="w-4 h-4 inline mr-2" />
              Profesional • Bersertifikat • Terpercaya
            </Badge>

            <h1 className="display-large">
              Solusi Pengangkutan<br />
              <span className="text-text-secondary">Limbah B3</span> Terpercaya
            </h1>

            <p className="body-large text-text-secondary">
              Melayani pengangkutan limbah Bahan Berbahaya & Beracun (B3) dengan standar keamanan tinggi,
              legal, dan ramah lingkungan untuk industri, rumah sakit, dan laboratorium di seluruh Jawa Barat.
            </p>

            <div className="flex flex-wrap gap-4 pt-4">
              <Button
                onClick={handleWhatsAppClick}
                className="btn-cta"
              >
                <Phone className="w-5 h-5 mr-2" />
                Konsultasi Gratis Sekarang
              </Button>
              <Button
                variant="outline"
                className="btn-secondary"
                onClick={() => document.getElementById('layanan').scrollIntoView({ behavior: 'smooth' })}
              >
                Lihat Layanan Kami
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 bg-brand-dark text-white">
        <div className="container max-w-6xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center animated zoomIn" style={{ animationDelay: `${index * 100}ms` }}>
                <div className="w-16 h-16 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
                  <stat.icon className="w-8 h-8 text-brand-dark" />
                </div>
                <div className="text-4xl font-bold mb-2 text-brand-primary">{stat.number}</div>
                <div className="body-medium opacity-90">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-4 bg-subtle dark:bg-gray-800 abstract-bg">
        <div className="container max-w-6xl mx-auto relative z-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <Card key={index} className="network-card text-center animated fadeIn delay-200ms">
                <CardContent className="pt-6">
                  <div className="w-16 h-16 bg-brand-primary rounded-full flex items-center justify-center mx-auto mb-4">
                    <feature.icon className="w-8 h-8 text-brand-dark" />
                  </div>
                  <h3 className="heading-3 mb-2">{feature.title}</h3>
                  <p className="body-small text-text-secondary">{feature.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Company Profile */}
      <section id="tentang" className="py-20 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="animated fadeIn">
              <img
                src="https://images.unsplash.com/photo-1699078109661-0269200668ae"
                alt="Environmental Care"
                className="rounded-3xl shadow-lg hover-lift"
                loading="lazy"
                decoding="async"
              />
            </div>
            <div className="space-y-6 animated fadeIn delay-200ms">
              <Badge className="bg-brand-dark text-white">Tentang Kami</Badge>
              <h2 className="display-medium">
                Komitmen Kami untuk Lingkungan Lebih Baik
              </h2>
              <p className="body-medium text-text-secondary">
                PT Alam Hijau Pertiwi adalah perusahaan jasa pengangkutan limbah B3 yang aman dan terpercaya, telah terakreditasi oleh
                Kementerian Lingkungan Hidup dan Kehutanan (KLHK) Republik Indonesia.
                Dengan mengantongi Izin Penyelenggaraan Pengangkutan Limbah B3 dari Dirjen Perhubungan Darat,
                kami berkomitmen menyediakan layanan transportasi ramah lingkungan,
                khususnya untuk kategori limbah B3 Medis dan Industri.
              </p>
              <div className="space-y-3">
                {[
                  'Berpengalaman 15+ tahun menangani berbagai jenis limbah B3',
                  'Armada khusus dengan teknologi tracking real-time',
                  'Tim profesional terlatih dan bersertifikat K3',
                  'Dokumentasi lengkap sesuai regulasi terbaru',
                  'Coverage area seluruh Jawa Barat',
                  'Response time 24/7 untuk emergency service'
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3">
                    <CheckCircle2 className="w-6 h-6 text-brand-primary flex-shrink-0 mt-0.5" />
                    <span className="body-medium">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Visi-Misi Section */}
      <section className="py-20 px-4 bg-section dark:bg-gray-800 abstract-bg">
        <div className="container max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-brand-dark text-white mb-4">Visi & Misi</Badge>
            <h2 className="display-medium mb-4">Komitmen Kami untuk Masa Depan</h2>
            <p className="body-large text-text-secondary max-w-2xl mx-auto">
              Visi dan misi yang menjadi pedoman kami dalam memberikan layanan terbaik
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Visi Card */}
            <Card className="network-card hover-lift">
              <CardHeader>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-brand-primary rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Target className="w-8 h-8 text-brand-dark" />
                  </div>
                  <div>
                    {/* <Badge className="bg-brand-primary text-brand-dark mb-2">Visi</Badge> */}
                    <CardTitle className="network-card-title text-xl">Visi Perusahaan</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 items-start">
                  <CheckCircle2 className="w-6 h-6 text-brand-primary flex-shrink-0 mt-1" />
                  <p className="body-medium text-text-secondary">
                    Menjadi Penyedia Jasa Transportasi Pilihan Dengan Harga Yang Bersaing
                  </p>
                </div>
                <div className="flex gap-3 items-start">
                  <CheckCircle2 className="w-6 h-6 text-brand-primary flex-shrink-0 mt-1" />
                  <p className="body-medium text-text-secondary">
                    Menjadi Mitra Terpercaya dalam mengelola Limbah B3 dengan penjemputan yang tepat waktu
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Misi Card */}
            <Card className="network-card hover-lift">
              <CardHeader>
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-16 h-16 bg-brand-primary rounded-2xl flex items-center justify-center flex-shrink-0">
                    <Zap className="w-8 h-8 text-brand-dark" />
                  </div>
                  <div>
                    {/* <Badge className="bg-brand-primary text-brand-dark mb-2">Misi</Badge> */}
                    <CardTitle className="network-card-title text-xl">Misi Perusahaan</CardTitle>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-3 items-start">
                  <CheckCircle2 className="w-6 h-6 text-brand-primary flex-shrink-0 mt-1" />
                  <p className="body-medium text-text-secondary">
                    Penyedia Jasa Transporter terdepan dalam mengelola limbah B3 dengan mengantongi izin resmi
                  </p>
                </div>
                <div className="flex gap-3 items-start">
                  <CheckCircle2 className="w-6 h-6 text-brand-primary flex-shrink-0 mt-1" />
                  <p className="body-medium text-text-secondary">
                    Memberikan kontribusi yang signifikan dalam mengelola limbah B3 dengan laporan pembuangan serta dokumen Pengelolaan resmi & terjamin
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us Section - WITH PARALLAX */}
      <section className="py-20 px-4 parallax-section">
        <img
          src="https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/bg-2.webp"
          alt="Environmental Protection"
          className="parallax-bg"
          loading="lazy"
          decoding="async"
        />
        <div className="parallax-overlay"></div>

        <div className="container max-w-6xl mx-auto parallax-content">
          <div className="text-center mb-12">
            <Badge className="bg-brand-primary text-brand-dark mb-4">Keunggulan Kami</Badge>
            <h2 className="display-medium mb-4 text-white">Mengapa Memilih Kami?</h2>
            <p className="body-large text-white/90 max-w-2xl mx-auto">
              Kepercayaan ratusan perusahaan di Jawa Barat adalah bukti komitmen kami dalam layanan terbaik
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {whyChooseUs.map((item, index) => (
              <Card key={index} className={`network-card bg-white/95 dark:bg-gray-700/95 backdrop-blur-sm ${index >= 3 ? 'lg:col-span-1' : ''}`}>
                <CardContent className="pt-6">
                  <div className="flex gap-4">
                    <div className="w-14 h-14 bg-brand-primary rounded-2xl flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-7 h-7 text-brand-dark" />
                    </div>
                    <div>
                      <h3 className="heading-3 mb-2 text-brand-dark dark:text-white">{item.title}</h3>
                      <p className="body-medium text-brand-dark dark:text-gray-100">{item.desc}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Waste Types Section - Enhanced */}
      <section id="layanan" className="py-20 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-brand-dark text-white mb-4">Layanan Kami</Badge>
            <h2 className="display-medium mb-4">Jenis Limbah B3 yang Kami Tangani</h2>
            <p className="body-large text-text-secondary max-w-2xl mx-auto">
              Melayani pengangkutan berbagai kategori limbah B3 dari industri, medis, dan laboratorium
              dengan prosedur keamanan tertinggi
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {wasteTypes.map((waste, index) => (
              <Card key={index} className="network-card hover-lift overflow-hidden group">
                <div className="h-40 overflow-hidden">
                  <img
                    src={waste.image}
                    alt={waste.title}
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                    loading="lazy"
                    decoding="async"
                  />
                </div>
                <CardHeader>
                  <div className="w-12 h-12 bg-brand-primary rounded-2xl flex items-center justify-center mb-3">
                    <waste.icon className="w-6 h-6 text-brand-dark" />
                  </div>
                  <CardTitle className="network-card-title text-lg">{waste.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <p className="body-small text-text-secondary">{waste.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section id="proses" className="py-20 px-4 bg-section dark:bg-gray-800 abstract-bg">
        <div className="container max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-brand-dark text-white mb-4">Alur Kerja</Badge>
            <h2 className="display-medium mb-4">Proses Pengangkutan Limbah B3</h2>
            <p className="body-large text-text-secondary max-w-2xl mx-auto">
              6 langkah sistematis untuk memastikan pengangkutan limbah B3 yang aman dan compliant
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {processSteps.map((step, index) => (
              <Card key={index} className="network-card relative">
                <CardContent className="pt-6">
                  <div className="absolute top-4 right-4 text-6xl font-bold text-brand-primary opacity-20">
                    {step.step}
                  </div>
                  <div className="w-14 h-14 bg-brand-primary rounded-2xl flex items-center justify-center mb-4">
                    <step.icon className="w-7 h-7 text-brand-dark" />
                  </div>
                  <h3 className="heading-3 mb-3">{step.title}</h3>
                  <p className="body-medium text-text-secondary">{step.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Gallery/Documentation Section */}
      <section id="galeri" className="py-20 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-brand-dark text-white mb-4">Dokumentasi</Badge>
            <h2 className="display-medium mb-4">Galeri Kegiatan Operasional</h2>
            <p className="body-large text-text-secondary max-w-2xl mx-auto">
              Dokumentasi proses kerja tim profesional kami dalam menangani limbah B3
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {galleryImages.map((item, index) => (
              <div key={index} className="relative group overflow-hidden rounded-3xl shadow-lg hover-lift">
                <img
                  src={item.url}
                  alt={item.title}
                  className="w-full h-64 object-cover transition-transform duration-300 group-hover:scale-110"
                  loading="lazy"
                  decoding="async"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-brand-dark/90 via-brand-dark/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <h3 className="font-semibold text-lg mb-1">{item.title}</h3>
                    <p className="text-sm opacity-90">{item.desc}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Certifications Section */}
      {/* Sertifikasi Section */}
      <section id="sertifikasi" className="py-20 px-4 bg-gray-100 dark:bg-gray-800">
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-brand-dark text-white mb-4">Legalitas Resmi</Badge>
            <h2 className="display-medium mb-4 text-brand-dark dark:text-white">Bersertifikat & Legal</h2>
            <p className="body-large text-text-secondary max-w-2xl mx-auto">
              Kami memiliki izin lengkap dan legalitas resmi untuk operasional pengangkutan limbah B3 di seluruh Indonesia
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {certifications.map((cert, index) => (
              <div 
                key={index} 
                className="group bg-white dark:bg-gray-700 rounded-3xl shadow-lg hover:shadow-xl transition-all duration-300 hover:-translate-y-2 overflow-hidden"
              >
                {/* Card Content */}
                <div className="p-8 text-center">
                  {/* Green Circle with Icon */}
                  <div className="w-24 h-24 mx-auto mb-6 bg-gradient-to-br from-green-400 to-green-500 rounded-full flex items-center justify-center shadow-lg">
                    <cert.icon className="w-12 h-12 text-brand-dark" strokeWidth={1.5} />
                  </div>
                  
                  {/* Year Badge */}
                  <div className="inline-block mb-4">
                    <span className="px-4 py-1 bg-green-200 text-green-800 text-sm font-semibold rounded-md">
                      {cert.year}
                    </span>
                  </div>
                  
                  {/* Title */}
                  <h3 className="text-xl font-bold text-brand-dark dark:text-white mb-3" style={{ fontStyle: 'italic' }}>
                    {cert.title}
                  </h3>
                  
                  {/* Detail/Number */}
                  <p className="text-sm text-gray-600 dark:text-gray-300 break-all" style={{ fontStyle: 'italic' }}>
                    {cert.detail}
                  </p>
                  
                  {/* Subtitle if exists */}
                  {cert.subtitle && (
                    <p className="text-xs text-brand-primary mt-2 font-medium" style={{ fontStyle: 'italic' }}>
                      {cert.subtitle}
                    </p>
                  )}
                </div>
              </div>
            ))}
          </div>
          
          {/* Additional trust badge */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-3 bg-white dark:bg-gray-700 rounded-full px-6 py-3 shadow-lg">
              <ShieldCheck className="w-6 h-6 text-brand-primary" />
              <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
                Semua dokumen terverifikasi dan dapat divalidasi
              </span>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimoni" className="py-20 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Badge className="bg-brand-dark text-white mb-4">Testimoni</Badge>
            <h2 className="display-medium mb-4">Dipercaya Berbagai Institusi</h2>
            <p className="body-large text-text-secondary max-w-2xl mx-auto">
              Testimoni dari klien yang telah mempercayakan pengelolaan limbah B3 kepada kami
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="network-card">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-brand-primary text-brand-primary" />
                    ))}
                  </div>
                  <p className="body-medium text-text-secondary mb-6 italic">
                    "{testimonial.text}"
                  </p>
                  <div className="flex items-start gap-3">
                    <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <Building2 className="w-6 h-6 text-brand-dark" />
                    </div>
                    <div>
                      <p className="font-semibold text-text-primary">{testimonial.person}</p>
                      <p className="body-small text-text-secondary">{testimonial.position}</p>
                      <p className="body-small font-semibold text-brand-dark">{testimonial.company}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Klien Kami Section */}
      <section id="klien" className="py-20 px-4 bg-gradient-to-br from-gray-50 to-green-50 dark:from-gray-800 dark:to-gray-900 relative overflow-hidden">
        {/* Abstract Background Pattern */}
        <div className="absolute inset-0 opacity-10">
          <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
            <defs>
              <pattern id="grid-pattern" width="10" height="10" patternUnits="userSpaceOnUse">
                <path d="M 10 0 L 0 0 0 10" fill="none" stroke="currentColor" strokeWidth="0.5" className="text-brand-primary"/>
              </pattern>
            </defs>
            <rect width="100" height="100" fill="url(#grid-pattern)" />
          </svg>
        </div>
        
        {/* Decorative Map Pins */}
        <div className="absolute top-10 left-10 w-8 h-8 text-brand-primary opacity-20 animate-bounce" style={{animationDelay: '0s'}}>
          <MapPin className="w-full h-full" />
        </div>
        <div className="absolute top-20 right-20 w-6 h-6 text-brand-primary opacity-20 animate-bounce" style={{animationDelay: '0.5s'}}>
          <MapPin className="w-full h-full" />
        </div>
        <div className="absolute bottom-20 left-1/4 w-10 h-10 text-brand-primary opacity-20 animate-bounce" style={{animationDelay: '1s'}}>
          <MapPin className="w-full h-full" />
        </div>

        <div className="container max-w-6xl mx-auto relative z-10">
          <div className="text-center mb-12">
            <Badge className="bg-brand-dark text-white mb-4">Jangkauan Layanan</Badge>
            <h2 className="display-medium mb-4 text-brand-dark dark:text-white" style={{
              fontStyle: 'italic',
              fontWeight: 'bold',
              textShadow: '2px 2px 4px rgba(0,0,0,0.1)'
            }}>
              KLIEN KAMI
            </h2>
            <p className="body-large text-text-secondary max-w-2xl mx-auto">
              Melayani lebih dari <span className="font-bold text-brand-primary">761+ klien</span> di berbagai wilayah Jawa Barat, Jawa Tengah, dan sekitarnya
            </p>
          </div>

          {/* Client Location Grid - Map Style */}
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4">
            {clientLocations.map((location, index) => (
              <div 
                key={index} 
                className="group relative animated fadeIn"
                style={{ animationDelay: `${index * 50}ms` }}
              >
                <div className="bg-gradient-to-br from-yellow-400 to-yellow-500 rounded-xl shadow-lg overflow-hidden transform transition-all duration-300 hover:scale-105 hover:shadow-xl border-2 border-brand-dark/20 relative">
                  {/* Map Silhouette Overlay - appears on hover */}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-all duration-500 pointer-events-none overflow-hidden">
                    <svg 
                      className="absolute w-full h-full"
                      viewBox="0 0 80 80" 
                      preserveAspectRatio="xMidYMid slice"
                    >
                      <defs>
                        <linearGradient id={`mapGrad-${index}`} x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor="#004534" stopOpacity="0.15" />
                          <stop offset="100%" stopColor="#2FA065" stopOpacity="0.25" />
                        </linearGradient>
                      </defs>
                      <path 
                        d={mapSilhouettes[location.city]} 
                        fill={`url(#mapGrad-${index})`}
                        stroke="#004534"
                        strokeWidth="1"
                        strokeOpacity="0.3"
                        className="transform scale-110 translate-x-2"
                      />
                      {/* Additional decorative elements */}
                      <circle cx="40" cy="35" r="3" fill="#004534" fillOpacity="0.4" className="animate-ping" style={{animationDuration: '2s'}} />
                      <circle cx="40" cy="35" r="2" fill="#004534" fillOpacity="0.6" />
                    </svg>
                  </div>
                  
                  {/* City Name Header */}
                  <div className="bg-brand-dark/90 px-3 py-2 relative z-10">
                    <h3 className="text-white font-bold text-sm text-center uppercase tracking-wide truncate">
                      {location.city}
                    </h3>
                  </div>
                  {/* Customer Count */}
                  <div className="px-3 py-3 text-center relative z-10">
                    <div className="flex items-center justify-center gap-1">
                      <span className="text-brand-dark font-bold text-lg">{location.customers}</span>
                      <span className="text-brand-dark/80 text-xs font-medium">CUSTOMER</span>
                    </div>
                  </div>
                  {/* Hover Pin Effect */}
                  <div className="absolute -top-2 -right-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 z-20">
                    <div className="w-6 h-6 bg-red-500 rounded-full flex items-center justify-center shadow-lg animate-pulse">
                      <MapPin className="w-4 h-4 text-white" />
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Total Summary */}
          <div className="mt-12 text-center">
            <div className="inline-flex items-center gap-4 bg-white dark:bg-gray-800 rounded-full px-8 py-4 shadow-xl border-2 border-brand-primary">
              <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center">
                <Users className="w-6 h-6 text-brand-dark" />
              </div>
              <div className="text-left">
                <p className="text-3xl font-bold text-brand-dark dark:text-white">761+</p>
                <p className="text-sm text-text-secondary">Total Klien Terlayani</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section - WITH PARALLAX */}
      <section className="py-24 px-4 parallax-section">
        <img
          src="https://nemuftsdmjzkzcygkjpg.supabase.co/storage/v1/object/public/assets-devaq/alam-hijau/bg-3.webp"
          alt="Waste Management Environment"
          className="parallax-bg"
          loading="lazy"
          decoding="async"
        />
        <div className="parallax-overlay" style={{
          background: 'linear-gradient(to bottom, rgba(0, 69, 52, 0.9), rgba(77, 184, 127, 0.85))'
        }}></div>

        <div className="container max-w-4xl mx-auto text-center parallax-content">
          <div className="space-y-6">
            <Badge className="bg-white text-brand-dark text-lg px-6 py-3 shadow-lg">
              <Phone className="w-5 h-5 inline mr-2" />
              Hubungi Kami Sekarang
            </Badge>
            <h2 className="display-medium text-white">
              Siap Bekerja Sama dengan Kami?
            </h2>
            <p className="body-large text-white/95">
              Konsultasikan kebutuhan pengangkutan limbah B3 Anda dengan tim profesional kami.
              Dapatkan penawaran terbaik dan solusi yang sesuai dengan regulasi.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
              <Button
                onClick={handleWhatsAppClick}
                className="btn-cta bg-white text-brand-dark hover:bg-brand-primary"
                size="lg"
              >
                <Phone className="w-5 h-5 mr-2" />
                Chat WhatsApp Sekarang
              </Button>
              <Button
                variant="outline"
                className="btn-secondary border-white text-white hover:bg-white hover:text-brand-dark"
                size="lg"
                onClick={() => document.getElementById('kontak').scrollIntoView({ behavior: 'smooth' })}
              >
                <MapPin className="w-5 h-5 mr-2" />
                Lihat Lokasi Kami
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="kontak" className="py-20 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <div className="space-y-6">
              <Badge className="bg-brand-dark text-white">Kontak Kami</Badge>
              <h2 className="display-medium">Hubungi Kami</h2>
              <p className="body-medium text-text-secondary">
                Tim kami siap membantu Anda dengan solusi pengangkutan limbah B3 yang aman dan terpercaya.
                Layanan konsultasi gratis untuk Anda.
              </p>

              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <MapPin className="w-6 h-6 text-brand-dark" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">Alamat</h3>
                    <p className="body-medium text-text-secondary">
                      Jl. AH Nasution No.298, Cipari, Kec. Mangkubumi,<br />
                      Kab. Tasikmalaya, Jawa Barat 46181
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Phone className="w-6 h-6 text-brand-dark" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">Telepon / WhatsApp</h3>
                    <p className="body-medium text-text-secondary">+62 852-9417-4856</p>
                    <p className="body-small text-text-light">Senin - Sabtu: 08:00 - 17:00 WIB</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-brand-primary rounded-full flex items-center justify-center flex-shrink-0">
                    <Clock className="w-6 h-6 text-brand-dark" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-text-primary mb-1">Emergency Service</h3>
                    <p className="body-medium text-text-secondary">24/7 Response Time</p>
                    <p className="body-small text-text-light">Untuk kebutuhan mendesak</p>
                  </div>
                </div>
              </div>

              <Button
                onClick={handleWhatsAppClick}
                className="btn-primary w-full md:w-auto"
              >
                <Phone className="w-5 h-5 mr-2" />
                Chat WhatsApp
              </Button>
            </div>

            <Card className="network-card">
              <CardContent className="pt-6">
                <div className="aspect-video bg-subtle rounded-2xl overflow-hidden">
                  <iframe
                    src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d5248.599609028864!2d108.18449564608832!3d-7.346039797921198!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e6f570304687697%3A0xd5613528fd1e54b7!2sPT%20ALAM%20HIJAU%20PERTIWI!5e0!3m2!1sen!2sid!4v1771330055957!5m2!1sen!2sid"
                    width="100%"
                    height="100%"
                    style={{ border: 0 }}
                    allowFullScreen=""
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title="PT Alam Hijau Pertiwi Location"
                  ></iframe>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-brand-dark text-white py-12 px-4">
        <div className="container max-w-6xl mx-auto">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
            <div className="md:col-span-2">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-brand-primary rounded-full flex items-center justify-center">
                  <Leaf className="w-6 h-6 text-brand-dark" />
                </div>
                <span className="font-bold text-lg">PT ALAM HIJAU PERTIWI</span>
              </div>
              <p className="body-small opacity-90 mb-4">
                Solusi profesional pengangkutan limbah B3 yang aman, legal, dan ramah lingkungan.
                Melayani industri, rumah sakit, dan laboratorium sejak 2008.
              </p>
              <div className="flex gap-2">
                <Badge className="bg-brand-primary text-brand-dark">ISO 14001</Badge>
                <Badge className="bg-brand-primary text-brand-dark">SMK3</Badge>
                <Badge className="bg-brand-primary text-brand-dark">Legal</Badge>
              </div>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Layanan</h3>
              <ul className="space-y-2 body-small opacity-90">
                <li>Limbah Industri</li>
                <li>Limbah Medis</li>
                <li>Limbah Laboratorium</li>
                <li>Limbah Elektronik</li>
                <li>Konsultasi B3</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold mb-4">Kontak</h3>
              <ul className="space-y-2 body-small opacity-90">
                <li>Jl. AH Nasution No.298</li>
                <li>Tasikmalaya, Jawa Barat</li>
                <li>+62 852-9417-4856</li>
                <li>24/7 Emergency</li>
              </ul>
            </div>
          </div>

          <div className="border-t border-white/20 pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="body-small opacity-75">
              &copy; 2024 PT Alam Hijau Pertiwi. All rights reserved.
            </p>
            <p className="body-small opacity-75">
              Berizin Resmi Kementerian Lingkungan Hidup & Kehutanan
            </p>
          </div>

          <div className="mt-6 text-center border-t border-white/20 pt-6">
            <p className="body-small opacity-75">
              Website dibuat oleh{' '}
              <a
                href="https://devaq.maulanamalik.my.id/"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-primary hover:text-white transition-colors font-semibold"
              >
                Davaq Webdev
              </a>
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
